/**
 * A button that shows its own pending state.
 *
 * Four screens had each hand-rolled the same swap — disable, and replace the
 * label with an indicator in the button's own foreground colour — with slightly
 * different accessibility wiring each time. The two things that are easy to get
 * wrong are both handled here: the button is disabled while busy, and it reports
 * `busy` to assistive technology rather than only looking different.
 */

import { ActivityIndicator } from 'react-native';

import { Button } from './ui/button';
import { Text } from './ui/text';
import { useAppearance } from '../providers/appearance';

/**
 * Renders a button that is disabled and shows a spinner while work is running.
 *
 * @param props Label, pending state, and handler.
 * @returns The button.
 */
export function ActionButton({
  label,
  busy,
  onPress,
  variant,
}: {
  label: string;
  busy: boolean;
  onPress: () => void;
  variant?: React.ComponentProps<typeof Button>['variant'];
}) {
  const { palette } = useAppearance();

  return (
    <Button
      onPress={onPress}
      disabled={busy}
      variant={variant}
      accessibilityLabel={label}
      aria-busy={busy}>
      {busy ? (
        <ActivityIndicator size="small" color={palette.primaryForeground} />
      ) : (
        <Text>{label}</Text>
      )}
    </Button>
  );
}
